import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { sanitizeSearchQuery } from './sanitizeQuery';
// Keep imports from types for consumers, but avoid unused import errors in this file

// One row in the Filings & Reports tab (mirrors backend FilingListItem).
export interface FilingListItem {
  report_id: string;
  fiscal_year: number;
  fiscal_period: string | null;
  period_end_date: string | null;
  form_type: string | null;
  source: string | null;             // "sec" | "companies_house" | "moneycontrol" | "nse"
  country: string | null;            // "US" | "INDIA" | "UK"
  currency: string | null;
  filing_date: string | null;
  document_url: string | null;       // UK Companies House PDF/iXBRL link
  source_url: string | null;         // UK source URL or India Moneycontrol slug
  revenue: number | null;
  net_income: number | null;
  created_at: string | null;
}

export interface CompanyFilingsResponse {
  company_id: string;
  company_name: string | null;
  country: string | null;
  currency: string | null;
  filings: FilingListItem[];
  total: number;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://graph-solution-backend.huhoka.com/api';

// Print the resolved API base URL to the console for debugging (build: 20260314v2)
// eslint-disable-next-line no-console
console.log('[api.ts] API_BASE_URL resolved to:', API_BASE_URL);


// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token storage helpers
const AUTH_TOKEN_KEY = 'auth_token';

export const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const setAuthToken = (token: string): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

export const clearAuthToken = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

// ── Global in-flight request tracking ──────────────────────────────────────
// A tiny pub/sub over the number of active axios requests, so a single top-of-
// page progress bar can indicate that *something* is being fetched without
// wiring a loading flag into every call site. Every request through this client
// increments on start and decrements on settle (success or error).
let activeRequests = 0;
const activityListeners = new Set<(count: number) => void>();

const emitActivity = (): void => {
  for (const listener of activityListeners) listener(activeRequests);
};

const startRequest = (): void => {
  activeRequests += 1;
  emitActivity();
};

const endRequest = (): void => {
  activeRequests = Math.max(0, activeRequests - 1);
  emitActivity();
};

/**
 * Subscribe to the count of in-flight API requests. The listener is invoked
 * immediately with the current count and again on every change. Returns an
 * unsubscribe function.
 */
export const subscribeToApiActivity = (
  listener: (count: number) => void
): (() => void) => {
  activityListeners.add(listener);
  listener(activeRequests);
  return () => {
    activityListeners.delete(listener);
  };
};

// Request interceptor - add auth token and adjust timeout for specific endpoints
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    startRequest();
    const token = getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Increase timeout for AI-powered endpoints (AI processing takes longer)
    // SEC research runs synchronously on backend — needs long timeout
    if (config.url?.includes('/companies/research') || config.url?.includes('company-research')) {
      config.timeout = 600000; // 10 minutes for SEC research (synchronous backend)
    }

    // US financial data pipeline (XBRL fetch + calculator + MongoDB/Neo4j save)
    if (config.url?.includes('/us-financial-data')) {
      config.timeout = 600000; // 10 minutes for full US pipeline
    }

    if (config.url?.includes('data-requirements/analyze')) {
      config.timeout = 180000; // 3 minutes for AI-powered data requirements analysis
    }

    // Company Intelligence: RSS multi-query fan-out + body enrichment for
    // 30-90 articles + single batched LLM call. Uncapped runs hit ~2-4 min.
    if (config.url?.includes('/company-intelligence/')) {
      config.timeout = 300000; // 5 minutes
    }

    // Document Insights: 2 sequential PDF downloads (MD&A + earnings call) +
    // 2 LLM analyses. Warm-cache hits return in <1s; cold requests with slow
    // IR/NSE pages can legitimately take 4-6 minutes. 10 min covers the tail.
    if (config.url?.includes('/analysis/documentInsights')) {
      config.timeout = 600000; // 10 minutes
    }

    if (config.url?.includes('system-research')) {
      config.timeout = 180000; // 3 minutes for AI-powered system research
    }

    if (config.url?.includes('business-domain/rewrite')) {
      config.timeout = 120000; // 2 minutes for AI business context rewriting
    }

    // Schema design endpoints (AI-powered, can take 1-3 minutes)
    if (config.url?.includes('schema-design') && config.url?.includes('/generate')) {
      config.timeout = 240000; // 4 minutes for schema generation
    }

    if (config.url?.includes('schema-design') && config.url?.includes('/evaluate')) {
      config.timeout = 180000; // 3 minutes for schema evaluation
    }

    if (config.url?.includes('schema-design') && config.url?.includes('/refine')) {
      config.timeout = 240000; // 4 minutes for schema refinement
    }

    // Schema design accept endpoints (can take several minutes for large schemas)
    if (config.url?.includes('schema-design') && (
      config.url?.includes('/accept-node') ||
      config.url?.includes('/accept-relationship') ||
      config.url?.includes('/accept-property')
    )) {
      config.timeout = 300000; // 5 minutes for accepting suggestions (large schemas need time for DDL generation)
    }

    // NLP Query endpoints (AI-powered, can take 30s - 2 minutes)
    if (config.url?.includes('nlp-query/query')) {
      config.timeout = 120000; // 2 minutes for NLP query processing
    }

    // Schema Mapping Analysis (AI-powered, can take 1-3 minutes)
    if (config.url?.includes('schema-mapping') && config.url?.includes('/analyze')) {
      config.timeout = 180000; // 3 minutes for schema mapping analysis
    }

    // Code Generation (AI-powered, can take 2-5 minutes)
    if (config.url?.includes('code-generation') && config.url?.includes('/generate')) {
      config.timeout = 300000; // 5 minutes for code generation
    }

    // Business Model endpoints (AI-powered, can take 3-5 minutes for LLM extraction + synthesis)
    if (config.url?.includes('business-model')) {
      config.timeout = 300000; // 5 minutes for business model extraction and synthesis
    }

    // Domain Pack AI endpoints (AI-powered subdomain suggestions, research)
    if (config.url?.includes('domain-packs') && (
      config.url?.includes('/suggestions') ||
      config.url?.includes('/research') ||
      config.url?.includes('/generate')
    )) {
      config.timeout = 120000; // 2 minutes for AI-powered domain pack operations
    }

    // Domain Pack Quality Assessment (can take 2-3 min per subdomain, 5+ min for multiple subdomains)
    if (config.url?.includes('domain-packs') && config.url?.includes('/assess')) {
      config.timeout = 300000; // 5 minutes for multi-subdomain quality assessment
    }

    // AI Assistant endpoints (AI-powered queries, can take 30s - 2 minutes)
    if (config.url?.includes('ai-assistant/query')) {
      config.timeout = 120000; // 2 minutes for AI Assistant queries
    }

    // Case Analysis endpoints — order matters: check most specific patterns first.
    // The earlier code had two problems:
    //   1. Duplicate `/suggest-domains` rules (one set 15min, then another overrode to 5min).
    //   2. A catch-all `else if (/analysis)` at the bottom that matched
    //      `/analysis/suggest-domains` and clobbered the 5-min timeout with 1 min,
    //      causing "timeout of 60000ms exceeded" on long-running domain suggestion runs.
    if (config.url?.includes('case-analysis') && config.url?.includes('/suggest-domains')) {
      config.timeout = 900000; // 15 minutes — 4-phase domain & hypothesis suggestion flow (LLM-heavy, 6-12 min for multi-question cases)
    } else if (config.url?.includes('case-analysis') && config.url?.includes('/approve-domain-selection')) {
      config.timeout = 120000; // 2 minutes for domain approval
    } else if (config.url?.includes('case-analysis') && config.url?.includes('/questions/analyze-all')) {
      config.timeout = 900000; // 15 minutes for batch question analysis
    } else if (config.url?.includes('case-analysis') && config.url?.includes('/questions') && config.url?.includes('/analyze')) {
      config.timeout = 300000; // 5 minutes for single question analysis
    } else if (config.url?.includes('case-analysis') && config.url?.includes('/questions') && config.method === 'get') {
      config.timeout = 180000; // 3 minutes for fetching questions list
    } else if (config.url?.includes('case-analysis') && config.url?.includes('/analysis')) {
      config.timeout = 60000; // 1 minute for plain analysis-results fetches (kept narrow — the else-if above catches /suggest-domains first)
    }

    // Domain Pack suggest-domains endpoint (Smart Wizard - AI-powered)
    if (config.url?.includes('domain-packs') && config.url?.includes('/suggest-domains')) {
      config.timeout = 120000; // 2 minutes for AI domain suggestions (Ollama fallback + OpenAI)
    }

    // Case Discovery AI endpoints (AI-powered research, can take 3-5 minutes)
    if (config.url?.includes('case-discovery/industry-research')) {
      config.timeout = 300000; // 5 minutes for AI industry research
    }

    // Industry Research v2.0 endpoints (comprehensive AI research, can take 3-5 minutes)
    if (config.url?.includes('/industries') && config.url?.includes('/research')) {
      config.timeout = 300000; // 5 minutes for comprehensive industry research
    }

    // Competitive landscape enrichment (SEC lookups for multiple companies, can take 2-3 minutes)
    if (config.url?.includes('/industries') && config.url?.includes('/competitive-landscape')) {
      config.timeout = 180000; // 3 minutes for competitive landscape operations (SEC lookups + AI analysis)
    }

    if (config.url?.includes('case-discovery/competition-analysis')) {
      config.timeout = 180000; // 3 minutes for AI competition analysis (generates 5 competitors with financials)
    }

    if (config.url?.includes('case-discovery/strategic-questions')) {
      config.timeout = 180000; // 3 minutes (same as competition analysis - generating personas + questions)
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    endRequest();
    return response;
  },
  (error: AxiosError) => {
    endRequest();
    if (error.response) {
      // Handle specific status codes
      switch (error.response.status) {
        case 401: {
          // Unauthorized - clear token and redirect to login.
          // EXCEPT for the auth endpoints themselves (login/register): a bad-
          // credentials 401 there must surface as an inline error on the page,
          // not trigger a full-page reload that discards the message.
          const reqUrl = error.config?.url ?? '';
          const isAuthEndpoint =
            reqUrl.includes('/auth/login') ||
            reqUrl.includes('/auth/register');
          if (!isAuthEndpoint) {
            clearAuthToken();
            window.location.href = '/login';
          }
          break;
        }
        case 403:
          // Forbidden
          console.error('Access forbidden:', error.response.data);
          break;
        case 404:
          // Not found
          console.error('Resource not found:', error.response.data);
          break;
        case 500:
          // Server error
          console.error('Server error:', error.response.data);
          break;
        default:
          console.error('API error:', error.response.data);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error: No response received');
    } else {
      // Something else happened
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Generic methods (default generic T = any) and optional axios config passthrough
  get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, { ...(config || {}), params } as AxiosRequestConfig).then((res) => res.data),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config).then((res) => res.data),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config).then((res) => res.data),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config).then((res) => res.data),

  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config).then((res) => res.data),

  // File upload with progress
  upload: (url: string, file: File, onProgress?: (progress: number) => void): Promise<unknown> => {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: import('axios').AxiosProgressEvent) => {
        if (onProgress && progressEvent.total && typeof progressEvent.loaded === 'number') {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    return apiClient.post(url, formData, config).then((res) => res.data);
  },
};

// Mapping Visualization API
export const mappingVisualizationAPI = {
  /**
   * Get consolidated mapping visualization data for a project
   */
  getMappingData: async (projectId: string) => {
    return api.get(`/data-files/projects/${projectId}/mapping-visualization`);
  },

  /**
   * Update a single column mapping
   */
  updateColumnMapping: async (
    projectId: string,
    fileId: string,
    columnName: string,
    mapping: {
      nodeLabel: string;
      propertyName: string;
      confidence?: number;
      userModified: boolean;
    }
  ) => {
    return api.put(
      `/data-files/projects/${projectId}/files/${fileId}/mappings/${columnName}`,
      mapping
    );
  },

  /**
   * Accept all AI-suggested mappings for a file
   */
  acceptAllMappings: async (projectId: string, fileId: string) => {
    return api.post(`/data-files/files/${fileId}/analysis/accept`, {
      projectId,
    });
  },

  /**
   * Generate data ingestion code for a case
   */
  generateIngestionCode: async (caseId: string) => {
    return api.post(`/code-generation/cases/${caseId}/ingestion-code`);
  },
};

// Annual Report Visualization API
export const annualReportAPI = {
  /**
   * Get annual report summary
   */
  getSummary: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/summary`);
  },

  /**
   * Get financial overview
   */
  getFinancialOverview: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/financial-overview`);
  },

  /**
   * Get revenue breakdown for Sankey diagram
   */
  getRevenueBreakdown: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/revenue-breakdown`);
  },

  /**
   * Get supply chain visualization data
   */
  getSupplyChainViz: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/supply-chain-viz`);
  },

  /**
   * Get value chain visualization data
   */
  getValueChainViz: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/value-chain-viz`);
  },

  /**
   * Get risk analysis data
   */
  getRiskAnalysis: async (reportId: string) => {
    return api.get(`/annual-reports/reports/${reportId}/risk-analysis`);
  },

  /**
   * Get graph data for interactive visualization
   */
  getGraphData: async (reportId: string, maxEntities?: number) => {
    const params = maxEntities ? `?max_entities=${maxEntities}` : '';
    return api.get(`/annual-reports/reports/${reportId}/graph-data${params}`);
  },
};

// SEC Reports API (Temporal Company Research)
export const secReportsAPI = {
  /**
   * Get all SEC reports for a company
   */
  getCompanyReports: async (caseId: string, companyId: string, limit?: number) => {
    const params = limit ? { limit } : {};
    return api.get(`/cases/${caseId}/companies/${companyId}/reports`, params);
  },

  /**
   * Get a specific SEC report
   */
  getReport: async (caseId: string, companyId: string, reportId: string) => {
    return api.get(`/cases/${caseId}/companies/${companyId}/reports/${reportId}`);
  },

  /**
   * Get revenue trend data
   */
  getRevenueTrend: async (caseId: string, companyId: string, periods: number = 8) => {
    return api.get(`/cases/${caseId}/companies/${companyId}/reports/trends/revenue`, { periods });
  },
};

// Standalone Companies API (Independent Company Research)
export const standaloneCompaniesAPI = {
  /**
   * Search for companies in SEC database.
   * The raw user input is sanitised — see lib/sanitizeQuery.ts — so every
   * search hits the backend with a consistent, special-character-free string.
   */
  searchSECCompanies: async (query: string) => {
    const sanitized = sanitizeSearchQuery(query);
    if (!sanitized) return [] as any[];
    return api.get(`/companies/search`, { query: sanitized });
  },

  /**
   * Get basic company info from SEC including SIC/industry
   * Useful for populating company selection fields
   */
  getCompanyBasicInfo: async (cik: string) => {
    return api.get(`/companies/sec/${cik}/basic-info`);
  },

  /**
   * Get available SEC reports for a company within a time period
   */
  getAvailableReports: async (cik: string, timePeriod: string = '2years') => {
    return api.get(`/companies/available-reports/${cik}`, { time_period: timePeriod });
  },

  /**
   * Start company research process (async, returns job ID)
   */
  startResearch: async (data: {
    cik: string;
    companyName: string;
    ticker?: string;
    timeRange?: string;
    selectedReports?: string[];
    companyId?: string;
  }) => {
    return api.post(`/companies/research`, data);
  },

  /**
   * Fetch US financial data via the unified pipeline
   * (XBRL fetch → calculator → MongoDB/Neo4j save)
   */
  fetchUSFinancialData: async (data: {
    company_name: string;
    company_code?: string;
    time_range?: string;
  }) => {
    return api.post(`/finance/us-financial-data`, data);
  },

  /**
   * Get research job status
   */
  getJobStatus: async (jobId: string) => {
    return api.get(`/companies/research/jobs/${jobId}`);
  },

  /**
   * Retry failed research job
   */
  retryJob: async (jobId: string) => {
    return api.post(`/companies/research/jobs/${jobId}/retry`);
  },

  /**
   * List all standalone companies (Research Library page)
   */
  listCompanies: async (signal?: AbortSignal) => {
    return api.get(`/companies`, undefined, signal ? { signal } : undefined);
  },

  /**
   * Permanently delete a company and all its financial data.
   */
  deleteCompany: async (companyId: string) => {
    return api.delete(`/companies/${companyId}`);
  },

  /**
   * Search ALL Company nodes in Neo4j by name substring.
   * No researchType filter — used by the competitor picker to find any company.
   */
  searchAllCompanies: async (q: string, signal?: AbortSignal) => {
    const sanitized = q.trim();
    if (!sanitized) return [] as any[];
    return api.get(`/companies/graph-search`, { q: sanitized }, signal ? { signal } : undefined);
  },

  /**
   * Get company details
   */
  getCompany: async (companyId: string) => {
    return api.get(`/companies/${companyId}`);
  },

  /**
   * Get UI configuration for company detail page.
   * Returns available tabs, feature flags, and region config.
   */
  getUIConfig: async (companyId: string) => {
    return api.get(`/companies/${companyId}/ui-config`);
  },

  /**
   * Get company reports
   */
  getCompanyReports: async (companyId: string) => {
    return api.get(`/companies/${companyId}/reports`);
  },

  /**
   * Country-agnostic Filings & Reports list. Returns flattened
   * Neo4j FinancialReport nodes — used by the India / UK tab.
   */
  getCompanyFilings: async (companyId: string): Promise<CompanyFilingsResponse> => {
    return api.get<CompanyFilingsResponse>(`/companies/${companyId}/filings`);
  },

  /**
   * Upload PDF for async extraction with real-time progress tracking
   */
  uploadPDFAsync: async (data: {
    file: File;
    companyName: string;
    fiscalYear?: number;
    ticker?: string;
    companyId?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('companyName', data.companyName);
    if (data.fiscalYear) {
      formData.append('fiscalYear', data.fiscalYear.toString());
    }
    if (data.ticker) {
      formData.append('ticker', data.ticker);
    }
    if (data.companyId) {
      formData.append('companyId', data.companyId);
    }

    return apiClient.post(`/pdf-uploads`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 1200000, // 20 minutes for PDF upload, preprocessing, and agentic extraction
    }).then((res) => res.data);
  },

  /**
   * Get PDF upload job status for progress tracking
   */
  getPDFUploadStatus: async (uploadId: string) => {
    return api.get(`/pdf-uploads/${uploadId}/status`);
  },

  /**
   * Get financial dashboard with Phase 1-4 metrics
   */
  getFinancialDashboard: async (companyId: string) => {
    return api.get(`/companies/${companyId}/financial-dashboard`);
  },

  /**
   * Get Business Model Canvas for a company's latest report
   * Returns hybrid XBRL + LLM extracted business model data
   */
  getBusinessModel: async (companyId: string, reportId?: string) => {
    const url = reportId
      ? `/companies/${companyId}/reports/${reportId}/business-model`
      : `/companies/${companyId}/business-model/latest`;
    return api.get(url);
  },

  /**
   * Refresh business model extraction using RAG-enhanced extraction
   * Bypasses job queue and directly generates business model data
   * Stores the result in Neo4j for future queries
   */
  refreshBusinessModel: async (companyId: string, reportId?: string) => {
    const url = `/companies/${companyId}/business-model/refresh`;
    return api.post(url, reportId ? { report_id: reportId } : {});
  },

  // ============================================================================
  // THREE-PHASE WORKFLOW - DISCOVERY PHASE
  // ============================================================================

  /**
   * Industry Research Endpoints
   */
  createIndustry: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/industry`, data);
  },

  getIndustry: async (caseId: string) => {
    return api.get(`/cases/${caseId}/industry`);
  },

  updateIndustry: async (caseId: string, data: any) => {
    return api.put(`/cases/${caseId}/industry`, data);
  },

  deleteIndustry: async (caseId: string) => {
    return api.delete(`/cases/${caseId}/industry`);
  },

  /**
   * Competitor Analysis Endpoints
   */
  createCompetitor: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/competitors`, data);
  },

  getAllCompetitors: async (caseId: string) => {
    return api.get(`/cases/${caseId}/competitors`);
  },

  getCompetitor: async (caseId: string, competitorId: string) => {
    return api.get(`/cases/${caseId}/competitors/${competitorId}`);
  },

  updateCompetitor: async (caseId: string, competitorId: string, data: any) => {
    return api.put(`/cases/${caseId}/competitors/${competitorId}`, data);
  },

  deleteCompetitor: async (caseId: string, competitorId: string) => {
    return api.delete(`/cases/${caseId}/competitors/${competitorId}`);
  },

  /**
   * Value Tree Endpoints
   */
  createValueNode: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/value-nodes`, data);
  },

  getAllValueNodes: async (caseId: string) => {
    return api.get(`/cases/${caseId}/value-nodes`);
  },

  getValueNode: async (caseId: string, valueNodeId: string) => {
    return api.get(`/cases/${caseId}/value-nodes/${valueNodeId}`);
  },

  updateValueNode: async (caseId: string, valueNodeId: string, data: any) => {
    return api.put(`/cases/${caseId}/value-nodes/${valueNodeId}`, data);
  },

  deleteValueNode: async (caseId: string, valueNodeId: string) => {
    return api.delete(`/cases/${caseId}/value-nodes/${valueNodeId}`);
  },

  /**
   * Opportunity Endpoints
   */
  createOpportunity: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/opportunities`, data);
  },

  getAllOpportunities: async (caseId: string) => {
    return api.get(`/cases/${caseId}/opportunities`);
  },

  updateOpportunity: async (caseId: string, opportunityId: string, data: any) => {
    return api.put(`/cases/${caseId}/opportunities/${opportunityId}`, data);
  },

  deleteOpportunity: async (caseId: string, opportunityId: string) => {
    return api.delete(`/cases/${caseId}/opportunities/${opportunityId}`);
  },

  /**
   * Challenge Endpoints
   */
  createChallenge: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/challenges`, data);
  },

  getAllChallenges: async (caseId: string) => {
    return api.get(`/cases/${caseId}/challenges`);
  },

  updateChallenge: async (caseId: string, challengeId: string, data: any) => {
    return api.put(`/cases/${caseId}/challenges/${challengeId}`, data);
  },

  deleteChallenge: async (caseId: string, challengeId: string) => {
    return api.delete(`/cases/${caseId}/challenges/${challengeId}`);
  },

  /**
   * Persona Endpoints
   */
  createPersona: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/personas`, data);
  },

  getAllPersonas: async (caseId: string) => {
    return api.get(`/cases/${caseId}/personas`);
  },

  updatePersona: async (caseId: string, personaId: string, data: any) => {
    return api.put(`/cases/${caseId}/personas/${personaId}`, data);
  },

  deletePersona: async (caseId: string, personaId: string) => {
    return api.delete(`/cases/${caseId}/personas/${personaId}`);
  },

  /**
   * Strategic Question Endpoints
   */
  createStrategicQuestion: async (caseId: string, personaId: string, data: any) => {
    return api.post(`/cases/${caseId}/personas/${personaId}/questions`, data);
  },

  getAllStrategicQuestions: async (caseId: string, personaId?: string) => {
    const url = personaId
      ? `/cases/${caseId}/personas/${personaId}/questions`
      : `/cases/${caseId}/strategic-questions`;
    return api.get(url);
  },

  updateStrategicQuestion: async (caseId: string, personaId: string, questionId: string, data: any) => {
    return api.put(`/cases/${caseId}/personas/${personaId}/questions/${questionId}`, data);
  },

  deleteStrategicQuestion: async (caseId: string, personaId: string, questionId: string) => {
    return api.delete(`/cases/${caseId}/personas/${personaId}/questions/${questionId}`);
  },

  // ============================================================================
  // THREE-PHASE WORKFLOW - ANALYSIS PHASE
  // ============================================================================

  /**
   * Data Element Endpoints
   */
  createDataElement: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/data-elements`, data);
  },

  getAllDataElements: async (caseId: string) => {
    return api.get(`/cases/${caseId}/data-elements`);
  },

  getDataElement: async (caseId: string, dataElementId: string) => {
    return api.get(`/cases/${caseId}/data-elements/${dataElementId}`);
  },

  updateDataElement: async (caseId: string, dataElementId: string, data: any) => {
    return api.put(`/cases/${caseId}/data-elements/${dataElementId}`, data);
  },

  deleteDataElement: async (caseId: string, dataElementId: string) => {
    return api.delete(`/cases/${caseId}/data-elements/${dataElementId}`);
  },

  /**
   * Data Mapping Endpoints
   */
  createDataMapping: async (caseId: string, data: any) => {
    return api.post(`/cases/${caseId}/data-mappings`, data);
  },

  getAllDataMappings: async (caseId: string) => {
    return api.get(`/cases/${caseId}/data-mappings`);
  },

  getDataMapping: async (caseId: string, mappingId: string) => {
    return api.get(`/cases/${caseId}/data-mappings/${mappingId}`);
  },

  updateDataMapping: async (caseId: string, mappingId: string, data: any) => {
    return api.put(`/cases/${caseId}/data-mappings/${mappingId}`, data);
  },

  deleteDataMapping: async (caseId: string, mappingId: string) => {
    return api.delete(`/cases/${caseId}/data-mappings/${mappingId}`);
  },

  // ============================================================================
  // Strategic Question Workflow Analysis (Milestones 1-4)
  // ============================================================================

  /**
   * Analyze a single strategic question with AI workflows
   * Integrates: Classification (M1-3) + Workflow Execution (M4)
   */
  analyzeStrategicQuestion: async (
    caseId: string,
    questionId: string,
    llmProvider?: 'openai' | 'anthropic'
  ) => {
    const endpoint = `/case-analysis/cases/${caseId}/questions/${questionId}/analyze`;
    return api.post(endpoint, {
      llm_provider: llmProvider,
    });
  },

  /**
   * Batch analyze all strategic questions for a case
   * Typical execution time: 2-10 minutes
   */
  analyzeAllStrategicQuestions: async (
    caseId: string,
    llmProvider?: 'openai' | 'anthropic'
  ) => {
    const endpoint = `/case-analysis/cases/${caseId}/questions/analyze-all`;
    return api.post(endpoint, {
      llm_provider: llmProvider,
    });
  },

  /**
   * Get analysis results for a strategic question
   * Returns classification data and workflow execution results
   */
  getQuestionAnalysis: async (caseId: string, questionId: string) => {
    const endpoint = `/case-analysis/cases/${caseId}/questions/${questionId}/analysis`;
    return api.get(endpoint);
  },
};

export const caseAnalysisAPI = {
  /**
   * Generate domain and hypothesis suggestions for a case
   * Takes 30-60 seconds - show loading screen during processing
   */
  suggestDomains: async (
    caseId: string,
    forceRegenerate: boolean = false,
    includeAiSuggestions: boolean = true,
    coverageThreshold: number = 0.7
  ) => {
    return api.post(`/case-analysis/cases/${caseId}/analysis/suggest-domains`, {
      case_id: caseId,
      force_regenerate: forceRegenerate,
      include_ai_suggestions: includeAiSuggestions,
      coverage_threshold: coverageThreshold
    });
  },

  /**
   * Approve domain and hypothesis selection
   * Creates CaseHypothesis nodes in Neo4j
   */
  approveDomainSelection: async (
    caseId: string,
    selectedExistingHypotheses: string[],
    selectedAiHypotheses: string[],
    customHypotheses: any[],
    approvedBy: string,
    notes?: string
  ) => {
    return api.post(`/case-analysis/cases/${caseId}/analysis/approve-domain-selection`, {
      case_id: caseId,
      selected_existing_hypotheses: selectedExistingHypotheses,
      selected_ai_hypotheses: selectedAiHypotheses,
      custom_hypotheses: customHypotheses,
      approved_by: approvedBy,
      notes: notes
    });
  },

  /**
   * Save selected enterprise systems for a case (Step 2 → Step 3)
   * Creates HAS_SELECTED_SYSTEM relationships in Neo4j
   */
  saveSelectedSystems: async (
    caseId: string,
    systemIds: string[],
    customSystems: string[] = []
  ) => {
    return api.post(`/case-analysis/cases/${caseId}/selected-systems`, {
      system_ids: systemIds,
      custom_systems: customSystems
    });
  },

  /**
   * Batch save question data canvases (Step 3 → Step 4)
   * Updates canvas status and approval in Neo4j
   */
  batchSaveCanvases: async (
    caseId: string,
    canvases: Array<{
      canvas_id: string;
      status: string;
      approved: boolean;
      consultant_notes?: string;
    }>
  ) => {
    return api.post(`/question-data-canvas/cases/${caseId}/canvases/batch-save`, {
      canvases
    });
  },

  /**
   * Generate and PERSIST a data canvas for one strategic question.
   *
   * Backend route: POST /question-data-canvas/cases/{caseId}/questions/{questionId}/canvas
   *
   * This is the server-side generator. It writes
   *   (:StrategicQuestion)-[:HAS_DATA_CANVAS]->(:QuestionDataCanvas)
   *                        -[:HAS_DATA_SOURCE]->(:CanvasDataSource {tablesJson})
   * which is what the Phase-3 projection turns into (:Case)-[:USES_TABLE]->(:DatabaseTable),
   * and therefore what Step 4 pre-populates from and Step 6 maps against.
   *
   * Step 3 previously built canvases in the browser and never called this, so
   * nothing was ever persisted: `batch-save` MATCHes on canvasId and silently
   * updated zero rows. Every downstream step then started from an empty graph.
   *
   * Returns counts only — call `getQuestionCanvas` to read the canvas back.
   */
  generateQuestionCanvas: async (
    caseId: string,
    questionId: string,
    selectedSystemIds: string[],
    autoDetectConnections: boolean = true
  ) => {
    return api.post(
      `/question-data-canvas/cases/${caseId}/questions/${questionId}/canvas`,
      {
        case_id: caseId,
        question_id: questionId,
        selected_system_ids: selectedSystemIds,
        auto_detect_connections: autoDetectConnections,
      }
    );
  },

  /**
   * Read one persisted canvas, including its data sources and connection points.
   *
   * Backend route: GET /question-data-canvas/cases/{caseId}/questions/{questionId}/canvas
   *
   * Node properties come back camelCase (canvasId, totalTables…) while the
   * nested `tables` are already snake_case — see `adaptServerCanvas` in
   * Step3DataMapping for the normalisation.
   *
   * 404 means "no canvas for this question yet", which is a normal state.
   */
  getQuestionCanvas: async (caseId: string, questionId: string) => {
    return api.get<any>(
      `/question-data-canvas/cases/${caseId}/questions/${questionId}/canvas`
    );
  },

  /**
   * Fetch the generated Cypher DDL for a case's schema design.
   * Returns the latest version's DDL plus schemaId. Backend route:
   *   GET /case-analysis/cases/{caseId}/schema/cypher-ddl
   * 404 when the case hasn't generated a schema yet — callers should treat
   * that as an empty state, not an error.
   */
  getSchemaCypherDDL: async (caseId: string) => {
    return api.get<{ schemaId: string; cypherDDL: string }>(
      `/case-analysis/cases/${caseId}/schema/cypher-ddl`
    );
  },

  /**
   * List all data mappings for a case (both approved and unapproved). Used by
   * MappingApprovalQueue to render the per-row approve/reject controls in
   * Step 3 of the Analysis wizard.
   *   GET /case-analysis/cases/{caseId}/mappings
   */
  listDataMappings: async (caseId: string) => {
    return api.get<DataMappingRecord[]>(`/case-analysis/cases/${caseId}/mappings`);
  },

  /**
   * Approve or reject a single data mapping. The backend's >=50% approval
   * threshold (enforced inside schema generation) is the same gate the
   * wizard's "Next" button uses, so the rate computed from listDataMappings
   * always matches the server-side check.
   *   PATCH /case-analysis/cases/{caseId}/mappings/{mappingId}/approve
   */
  approveDataMapping: async (
    caseId: string,
    mappingId: string,
    approved: boolean,
    notes?: string,
  ) => {
    return api.patch<DataMappingRecord>(
      `/case-analysis/cases/${caseId}/mappings/${mappingId}/approve`,
      { approved, notes: notes ?? null }
    );
  },

  /**
   * Lightweight aggregation of a case's DataMapping AI-validation status.
   * Aggregates server-side via Neo4j instead of transferring the full mapping
   * list, so it's the preferred call for the Step 3 → Step 4 gate.
   *
   * Mapping approval is now fully automatic: an AI critic validates and
   * approves every mapping in the background. This returns validation progress
   * (`ai_validated` / `flagged` / `validation_complete`). The legacy
   * `approved` / `approval_rate` / `meets_threshold` fields are retained.
   *   GET /case-analysis/cases/{caseId}/mappings/approval-status?threshold=0.5
   */
  getMappingApprovalStatus: async (
    caseId: string,
    threshold: number = 0.5,
  ): Promise<MappingApprovalStatus> => {
    return api.get<MappingApprovalStatus>(
      `/case-analysis/cases/${caseId}/mappings/approval-status`,
      { threshold },
    );
  },

  /**
   * Alias for getMappingApprovalStatus framed around AI validation. Use this
   * for the Step 3 gate / validation badges — it reads the same endpoint but
   * the intent (validation, not manual approval) is explicit at the call site.
   */
  getValidationStatus: async (caseId: string): Promise<MappingApprovalStatus> => {
    return api.get<MappingApprovalStatus>(
      `/case-analysis/cases/${caseId}/mappings/approval-status`,
    );
  },
};

/** Response shape of GET /case-analysis/cases/{id}/mappings/approval-status. */
export interface MappingApprovalStatus {
  case_id: string;
  total: number;
  approved: number;
  /** Count of mappings the AI critic has validated. */
  ai_validated?: number;
  /** Count of mappings the AI critic flagged or rejected (non-blocking). */
  flagged?: number;
  /** True once every mapping has been AI-validated (the Step 3 gate). */
  validation_complete?: boolean;
  /** Server-rounded to 4 decimals; 0.0 when total === 0. Retained legacy field. */
  approval_rate: number;
  threshold: number;
  meets_threshold: boolean;
}

// Shape of a DataMapping node as returned by /mappings list + /approve.
// Backend stores additional fields (transformationRules, rationale) but the
// UI only renders a stable subset — extra fields are passed through opaquely.
export interface DataMappingRecord {
  mappingId: string;
  sourceSystem?: string;
  sourceTable?: string;
  sourceColumn?: string;
  targetEntity?: string;
  targetProperty?: string;
  targetProperties?: string[];
  confidence?: number;
  confidenceLevel?: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  approved?: boolean;
  approvalDate?: string | null;
  notes?: string | null;
  rationale?: string | null;
  // AI background validation (fully-automatic approval flow).
  aiValidated?: boolean;
  validationVerdict?: 'approve' | 'flag' | 'reject' | string;
  validationRationale?: string | null;
  validatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// India Company Research API (MoneyControl JSON API / BSE / NSE)
export const indiaCompanyAPI = {
  /**
   * Fetch India company financial data via the MoneyControl JSON API (mcapi).
   * Responds synchronously with the full result in `data` (no polling).
   */
  triggerResearch: async (data: {
    company_name: string;
    data_type?: 'consolidated' | 'standalone';
  }) => {
    const { getWebhookToken } = await import('./webhookToken');
    const token = await getWebhookToken();
    return apiClient.post('/finance/india-financial-data-mcapi', data, {
      timeout: 600000,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then((res) => res.data as { status: string; message: string; data?: any; job_id?: string });
  },

  /**
   * Poll the status of an India research job.
   */
  pollStatus: async (jobId: string) => {
    return apiClient.get(`/finance/india-financial-data/${jobId}`, {
      timeout: 15000,
    }).then((res) => res.data as {
      job_id: string;
      status: 'processing' | 'completed' | 'failed';
      company_name: string;
      data?: any;
      error?: string | null;
      created_at: string;
      completed_at?: string | null;
    });
  },

  /**
   * Search for a company on Moneycontrol and return the top match.
   */
  searchCompany: async (companyName: string) => {
    return apiClient.post('/finance/search-company', { company_name: companyName }, {
      timeout: 10000,
    }).then((res) => res.data as {
      stock_name?: string;
      sc_id?: string;
      sc_type?: string;
      sc_sl_flag?: string;
      sc_bse_symbol?: string;
      sc_nse_symbol?: string;
      NSE_BId?: string;
      [key: string]: any;
    });
  },

  /**
   * India autocomplete search. Backend route: POST /api/finance/search-companies
   *
   * The backend now caches Yahoo Finance results in-memory for 5 minutes per
   * (query, limit) via `india_search_service`, and skips the slow Moneycontrol
   * probe enrichment by default — safe to call on every keystroke from a
   * debounced input (>= 2 chars). Pass `with_mc_probes=true` only when the
   * caller actually needs the MC routing fields downstream.
   */
  searchCompanies: async (companyName: string) => {
    const sanitized = sanitizeSearchQuery(companyName);
    if (!sanitized || sanitized.length < 2) {
      return { results: [], query: '', normalized_query: '' };
    }
    return apiClient.post('/finance/search-companies', { company_name: sanitized }, {
      timeout: 10000,
    }).then((res) => res.data as {
      results: Array<{
        stock_name: string;
        sc_id: string | null;
        sc_nse_symbol: string | null;
        sc_bse_symbol: string | null;
        /** Yahoo's classification, e.g. "Technology". Null when Yahoo omits it. */
        sector: string | null;
        /** Yahoo's finer classification, e.g. "Information Technology Services". */
        industry: string | null;
        [key: string]: any;
      }>;
      query: string;
      normalized_query: string;
    });
  },
};

// UK Company Research API (Companies House iXBRL pipeline)
//
// Backend route: POST /api/finance/uk-financial-data
// Returns an envelope: { status: "success"|"partial"|"error", message, data }
// where `data` includes metadata (company_id, company_number, ...) and year-wise records.
// The endpoint is synchronous — no polling required.
export const ukCompanyAPI = {
  /**
   * Lightweight availability probe — lists annual `accounts` filings on
   * Companies House without downloading documents. Used by the UI to
   * constrain time-range selectors based on actual data availability.
   *
   * Backend route: GET /api/finance/uk-availability
   */
  checkAvailability: async (params: {
    company_name: string;
    company_code?: string;
  }) => {
    const search = new URLSearchParams();
    search.append('company_name', params.company_name);
    if (params.company_code) search.append('company_code', params.company_code);
    return apiClient.get(`/finance/uk-availability?${search.toString()}`, {
      timeout: 20000,
    }).then((res) => res.data as {
      status: 'success' | 'not_found' | 'error';
      company_number: string | null;
      company_name: string | null;
      available_years: number[];
      filing_count: number;
      message: string;
    });
  },

  /**
   * Fast Companies House autocomplete search.
   * Backend route: GET /api/finance/uk/search-companies?q=...&limit=...
   *
   * The backend caches results in-memory for 5 minutes per (query, limit)
   * so repeated calls during typeahead are cheap and stay inside the
   * Companies House 600-req / 5-min rate limit. Designed to be called on
   * every keystroke from a debounced input (>= 2 chars).
   */
  searchCompanies: async (query: string, limit = 10) => {
    const sanitized = sanitizeSearchQuery(query);
    if (!sanitized || sanitized.length < 2) {
      return { results: [], count: 0, cached: false };
    }
    const search = new URLSearchParams();
    search.append('q', sanitized);
    search.append('limit', String(limit));
    return apiClient.get(`/finance/uk/search-companies?${search.toString()}`, {
      timeout: 10000,
    }).then((res) => res.data as {
      results: Array<{
        company_name: string;
        company_number: string;
        company_status?: string | null;
        company_type?: string | null;
        date_of_creation?: string | null;
        address_snippet?: string | null;
      }>;
      count: number;
      cached: boolean;
    });
  },

  triggerResearch: async (data: {
    company_name: string;
    company_code?: string;             // Companies House 8-digit number (optional)
    data_type?: 'annual';
    time_range?: '1year' | '2years' | '3years' | '5years' | '10years';
  }) => {
    const { getWebhookToken } = await import('./webhookToken');
    const token = await getWebhookToken();
    return apiClient.post('/finance/uk-financial-data', {
      company_name: data.company_name,
      company_code: data.company_code,
      data_type: data.data_type ?? 'annual',
      time_range: data.time_range ?? '5years',
    }, {
      timeout: 600000,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then((res) => res.data as {
      status: 'success' | 'partial' | 'error';
      message: string;
      data?: {
        sanitized_company_name?: string;
        metadata?: {
          company_number?: string;
          company_name?: string;
          company_id?: string;
          country?: string;
          currency?: string;
          source?: string;
          sector?: string;
          status?: string;
          reason?: string;
          [key: string]: any;
        };
        data?: any[];
        filings_processed?: number;
      } | null;
    });
  },
};

export default apiClient;
